import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { CIRCLE_CUBIC_SEGMENTS_72, CIRCLE_MOVE_TO_72 } from '@/lib/elements/circle-svg-path'
import { HEART_CUBIC_SEGMENTS_72, HEART_MOVE_TO_72 } from '@/lib/elements/heart-svg-path'
import type { PathCommand } from '@/lib/elements/types/path-command'
import type { PathCommandsForSidesOptions } from './types/path-commands-for-sides-options'
import { curveToCubic, lineTo, moveTo } from './helpers/path-command-builders' /**
 * Create a Path.commands array compatible with backend `decodePath`.
 *
 * Coordinates are in the element's local coordinate system: (0,0) .. (width,height).
 * We approximate curved shapes with line segments so we can support all current shapes
 * without needing arc/cubic parsing.
 */
export function shapeToPathCommands(type: ShapeType, width: number, height: number): PathCommand[] {
  const w = Math.max(0, width)
  const h = Math.max(0, height)

  switch (type) {
    case 'square': {
      return [
        { moveTo: { x: 0, y: 0 } },
        { lineTo: { x: w, y: 0 } },
        { lineTo: { x: w, y: h } },
        { lineTo: { x: 0, y: h } },
      ]
    }

    case 'triangle':
      return regularPolygonCommands(3, 0, w, h)

    case 'hexagon':
      return regularPolygonCommands(6, 0, w, h)

    case 'pentagon':
      return regularPolygonCommands(5, 0, w, h)

    case 'star':
      return regularPolygonCommands(5, 50, w, h)

    case 'circle': {
      const ext = 71
      const c = 36
      const norm = (x: number, y: number) => ({
        x: w / 2 + (x - c) * (w / ext),
        y: h / 2 + (y - c) * (h / ext),
      })
      const m = norm(CIRCLE_MOVE_TO_72.x, CIRCLE_MOVE_TO_72.y)
      const commands: PathCommand[] = [moveTo(m.x, m.y)]

      for (const seg of CIRCLE_CUBIC_SEGMENTS_72) {
        const c1 = norm(seg.cx1, seg.cy1)
        const c2 = norm(seg.cx2, seg.cy2)
        const end = norm(seg.x, seg.y)

        commands.push(curveToCubic(c1.x, c1.y, c2.x, c2.y, end.x, end.y))
      }

      return commands
    }

    case 'heart': {
      const ext = 71
      const norm = (x: number, y: number) => ({
        x: ((x - 0.5) / ext) * w,
        y: ((y - 0.5) / ext) * h,
      })
      const m = norm(HEART_MOVE_TO_72.x, HEART_MOVE_TO_72.y)
      const commands: PathCommand[] = [moveTo(m.x, m.y)]

      for (const seg of HEART_CUBIC_SEGMENTS_72) {
        const c1 = norm(seg.cx1, seg.cy1)
        const c2 = norm(seg.cx2, seg.cy2)
        const end = norm(seg.x, seg.y)

        commands.push(curveToCubic(c1.x, c1.y, c2.x, c2.y, end.x, end.y))
      }

      return commands
    }

    case 'starone':
      return regularPolygonCommands(10, 25, w, h)

    case 'startwo':
      return regularPolygonCommands(10, 60, w, h)

    default: {
      return [moveTo(0, 0), lineTo(w, 0), lineTo(w, h), lineTo(0, h)]
    }
  }
}

/**
 * Regular polygon (and star, when `insetPercent > 0`) vertex commands — matches legacy
 * `page-editor`'s `ODPath.prototype.buildPathSegments` (odeditorsvg.js) exactly: vertices are
 * inscribed in the width/height ellipse (`x = w/2 + w/2·sin(360/sides·i)`,
 * `y = h/2 - h/2·cos(360/sides·i)`), NOT stretched or forced to touch every edge of the
 * bounding box the way this codebase's older per-shape formulas did. `sides` is the number of
 * outer points (tips, for a star). `insetPercent` (0-100) reduces an apothem-scaled radius
 * (`(w/2)·cos(180/sides)`) for an inward "valley" vertex inserted between each outer point,
 * matching legacy's `wInner_radius = wr - wr·inset/100`; `insetPercent <= 0` omits the valley
 * points entirely (plain convex polygon), matching legacy's `if (this.inset > 0)` guard.
 */
export function regularPolygonCommands(
  sidesIn: number,
  insetPercent: number,
  width: number,
  height: number
): PathCommand[] {
  const sides = Math.max(3, Math.min(100, Math.round(sidesIn)))
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  const wRadius = w / 2
  const hRadius = h / 2
  const inset = Math.max(0, insetPercent)
  const halfStepDeg = 360 / sides / 2
  const apothemRatio = Math.cos((halfStepDeg * Math.PI) / 180)
  const wInner = wRadius * apothemRatio * (1 - inset / 100)
  const hInner = hRadius * apothemRatio * (1 - inset / 100)

  const pts: Array<{ x: number; y: number }> = []

  for (let i = 1; i <= sides; i++) {
    const angleDeg = (360 / sides) * i
    const angleRad = (angleDeg * Math.PI) / 180

    pts.push({
      x: wRadius + wRadius * Math.sin(angleRad),
      y: hRadius - hRadius * Math.cos(angleRad),
    })

    if (inset > 0) {
      const innerAngleRad = ((angleDeg + halfStepDeg) * Math.PI) / 180

      pts.push({
        x: wRadius + wInner * Math.sin(innerAngleRad),
        y: hRadius - hInner * Math.cos(innerAngleRad),
      })
    }
  }

  return [moveTo(pts[0].x, pts[0].y), ...pts.slice(1).map((p) => lineTo(p.x, p.y))]
}

/**
 * Build path commands from Object Effects "sides" (3–100) for polygon and star family shapes.
 * `innerRatio` (0-1, legacy's `content.inner` convention) is converted to `regularPolygonCommands`'s
 * `insetPercent` (0-100) via `(1 - innerRatio) * 100`.
 */
export function pathCommandsForSides(
  type: ShapeType,
  sides: number,
  width: number,
  height: number,
  innerRatio?: number,
  opts?: PathCommandsForSidesOptions
): PathCommand[] {
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  const ratioOverride =
    typeof innerRatio === 'number' && Number.isFinite(innerRatio)
      ? Math.max(0, Math.min(1, innerRatio))
      : null

  switch (type) {
    case 'square': {
      // 4 sides / no inset is ambiguous for a `square`-type shape, because two different shapes
      // report it:
      //
      //  - an untouched square/rectangle, which saves as kindId 1 (a plain RectangleElement —
      //    see `resolveKindId`) and must keep rendering as the axis-aligned rectangle already on
      //    canvas rather than flashing to a diamond mid-preview;
      //  - one already converted by Sides/Inset to a RegularPolygonElement (kindId 10), where 4
      //    sides is the meaningful *diamond* — `regularPolygonCommands(4, 0, ...)` inscribes in
      //    the bounds, putting a vertex at the midpoint of each edge.
      //
      // `asRegularPolygon` is the caller's answer to "which one is this?". Any other sides/inset
      // value is unambiguously a polygon either way.
      const insetPercent = ratioOverride != null ? (1 - ratioOverride) * 100 : 0

      if (!opts?.asRegularPolygon && sides === 4 && insetPercent <= 0) {
        return shapeToPathCommands(type, w, h)
      }

      return regularPolygonCommands(sides, insetPercent, w, h)
    }

    case 'triangle':
    case 'pentagon':
    case 'hexagon':
      return regularPolygonCommands(
        sides,
        ratioOverride != null ? (1 - ratioOverride) * 100 : 0,
        w,
        h
      )

    case 'star':
      return regularPolygonCommands(sides, (1 - (ratioOverride ?? 0.5)) * 100, w, h)
    case 'starone':
      return regularPolygonCommands(sides, (1 - (ratioOverride ?? 0.75)) * 100, w, h)
    case 'startwo':
      return regularPolygonCommands(sides, (1 - (ratioOverride ?? 0.4)) * 100, w, h)
    default:
      return shapeToPathCommands(type, w, h)
  }
}
